<template>
  <div class="history-item">
    <div class="indicator">
      <div class="bar" />
      <dot :selected="selected" />
    </div>
    <div class="content">
      <div class="heading">
        <p class="datetime body-2" v-if="item.CommitDate">
          {{ formattedDatetime(item.CommitDate) }}
        </p>
      </div>
      <p class="subtitle-2 title" v-if="item.ChangeTitle">
        {{ item.ChangeTitle }}
      </p>
      <tag class="promotion-tag" v-if="item.PromotionTag">{{
        item.PromotionTag
      }}</tag>
      <p class="caption details" v-if="item.ChangeDetails">
        {{ item.ChangeDetails }}
      </p>
      <div class="user" v-if="item.CommittedBy">
        <avatar :size="24" />
        <p class="body-2 name">{{ item.CommittedBy }}</p>
      </div>
      <v-menu offset-y>
        <template v-slot:activator="{ on, attrs }">
          <v-btn class="menu-btn" icon v-bind="attrs" v-on="on">
            <v-icon>mdi-dots-horizontal</v-icon>
          </v-btn>
        </template>

        <v-list>
          <v-list-item @click="openDetails">
            <v-list-item-title> View Change Details </v-list-item-title>
          </v-list-item>
          <v-list-item v-if="!currentVersion" @click="openRestore">
            <v-list-item-title> Restore </v-list-item-title>
          </v-list-item>
          <template
            v-if="currentVersion && nextEnvironment && !item.PromotionTag && hasPromotionAccess"
          >
            <v-list-item>
              <v-list-item-title @click="promote"
                >Promote to {{ capitalizedNextEnvironment }}</v-list-item-title
              >
            </v-list-item>
          </template>
        </v-list>
      </v-menu>
    </div>
    <detailsModal
      :isOpen="detailsModalOpen"
      :item="item"
      @editChange="editChange"
      @closeModal="closeDetails"
    />
    <restoreConfirmationModal
      :value="restoreConfirmationModalOpen"
      @restoreChange="restoreChange"
      @closeModal="closeRestore"
    />
  </div>
</template>
    
<script>
/**
 * @author Cameron Bulock <cameron.bulock@quavo.com>
 */
import { mapGetters } from "vuex";
import capitalize from "lodash/capitalize";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";

import avatar from "@/qux-common/qux22/components/molecules/avatar";
import detailsModal from "./details-modal";
import dot from "./dot";
import restoreConfirmationModal from "./restore-confirmation-modal";
import tag from "@/qux-common/qux22/components/molecules/tag";

dayjs.extend(localizedFormat);

export default {
  name: "HistoryItem",
  components: { avatar, detailsModal, dot, restoreConfirmationModal, tag },
  props: {
    item: {
      type: Object,
    },
    selected: {
      type: Boolean,
      default: false,
    },
    currentVersion: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      detailsModalOpen: false,
      restoreConfirmationModalOpen: false,
    };
  },
  methods: {
    formattedDatetime(datetime) {
      return dayjs(datetime).format("MMM D YYYY h:mma");
    },
    openDetails() {
      if (this.item.Version) this.detailsModalOpen = true;
    },
    closeDetails() {
      this.detailsModalOpen = false;
    },
    openRestore() {
      this.restoreConfirmationModalOpen = true;
    },
    closeRestore() {
      this.restoreConfirmationModalOpen = false;
    },
    editChange(args) {
      this.$emit("editChange", { Version: this.item.Version, ...args });
    },
    restoreChange(args) {
      this.$emit("restoreChange", { Version: this.item.Version, ...args });
    },
    promote() {
      this.$emit("promote");
    },
  },
  computed: {
    capitalizedNextEnvironment() {
      return capitalize(this.nextEnvironment);
    },
    hasPromotionAccess() {
      return this.accessRoles && this.accessRoles.includes("ConfigAdmin");
    },
    ...mapGetters({
      accessRoles: "getRoles",
      nextEnvironment: "getNextEnvironment",
    }),
  },
};
</script>
    
<style lang="scss" scoped>
.history-item {
  display: flex;
  padding: 0 0 0 $qvo-spacing-24;
  &::v-deep {
    .promotion-tag .v-chip__content .subtitle-2 {
      color: white;
    }
  }
  &:first-child {
    .bar {
      margin-top: 14px;
    }
    .content {
      padding-top: 0;
    }
  }
  &:last-child {
    .bar {
      height: 16px;
    }
  }
  &:hover {
    background: $qvo-color-blue-100;
  }
  .indicator {
    position: relative;
  }
  .bar {
    height: 100%;
    border-left: 2px solid $qvo-color-grey-200;
    padding: 0 $qvo-spacing-8 0 $qvo-spacing-4;
  }
  .dot {
    position: absolute;
    top: 14px;
    left: -3px;
  }
  .content {
    position: relative;
    width: 100%;
    padding-bottom: $qvo-spacing-16;
    overflow: hidden;
    .heading {
      display: flex;
      align-items: center;
      justify-content: space-between;
      min-height: 8px;
    }
    .datetime {
      padding-top: 10px;
      color: $qvo-color-grey-500;
      margin-bottom: $qvo-spacing-8;
    }
    .title {
      margin-bottom: $qvo-spacing-8;
      padding-right: $qvo-spacing-24;
    }
    .promotion-tag {
      margin-bottom: $qvo-spacing-8;
      background-color: $qvo-color-branding-400;
    }
    .caption {
      color: $qvo-color-grey-500;
      margin-bottom: $qvo-spacing-8;
    }
    .user {
      display: flex;
      align-items: center;
      gap: $qvo-spacing-8;
      padding-right: $qvo-spacing-24;
      .name {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
    .menu-btn {
      position: absolute;
      top: 0;
      right: 0;
    }
  }
}
</style>